import React from "react";

// ContractValueTable — per-leg breakdown view. Shows each leg's contract
// price, debit/credit, contribution to position P&L, and Greeks.
const fmt$ = (v, d = 2) =>
  Number.isFinite(v)
    ? `${v < 0 ? "-" : ""}$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })}`
    : "—";

const ContractValueTable = ({ legs = [], pricing = null, greeks = null }) => {
  if (!legs.length) {
    return (
      <div
        className="bg-[#0E1A30] border border-white/10 rounded-xl p-6 text-center text-white/45 text-[13px]"
        data-testid="forge-contract-table-empty"
      >
        Add legs from the option chain to see contract values.
      </div>
    );
  }

  return (
    <div
      className="bg-[#0E1A30] border border-white/10 rounded-xl overflow-hidden"
      data-testid="forge-contract-table"
    >
      <div className="px-4 py-2.5 border-b border-white/10 text-[11px] uppercase tracking-[0.22em] text-white/60">
        Contract Value · {legs.length} leg{legs.length === 1 ? "" : "s"}
      </div>
      <div className="overflow-auto">
        <table className="w-full text-[12px]">
          <thead className="text-[10px] uppercase tracking-[0.18em] text-white/40 border-b border-white/5">
            <tr>
              <th className="text-left py-2 px-3">Side</th>
              <th className="text-left py-2 px-3">Type</th>
              <th className="text-right py-2 px-3">Strike</th>
              <th className="text-right py-2 px-3">Expiry (DTE)</th>
              <th className="text-right py-2 px-3">Qty</th>
              <th className="text-right py-2 px-3">Price</th>
              <th className="text-right py-2 px-3">Cost / Credit</th>
            </tr>
          </thead>
          <tbody>
            {legs.map((l, i) => {
              const qty = Number(l.qty) || 1;
              // Try multiple sources for the per-contract price.
              const px = Number.isFinite(l.price)
                ? l.price
                : Number.isFinite(l.entry_price)
                  ? l.entry_price
                  : Number.isFinite(l.mid)
                    ? l.mid
                    : null;
              const sign = (l.action || "").toUpperCase() === "BUY" ? -1 : 1;
              const cost = Number.isFinite(px) ? sign * px * 100 * qty : null;
              return (
                <tr
                  key={i}
                  className="border-b border-white/[0.04] hover:bg-white/[0.02]"
                  data-testid={`forge-contract-row-${i}`}
                >
                  <td className={`py-2 px-3 font-medium ${
                    (l.action || "").toUpperCase() === "BUY" ? "text-[#22C55E]" : "text-[#EF4444]"
                  }`}>
                    {(l.action || "").toUpperCase()}
                  </td>
                  <td className="py-2 px-3 capitalize text-white/85">{l.type}</td>
                  <td className="py-2 px-3 text-right num text-white">{fmt$(l.strike)}</td>
                  <td className="py-2 px-3 text-right num text-white/55">{l.dte}d</td>
                  <td className="py-2 px-3 text-right num text-white/85">{qty}</td>
                  <td className="py-2 px-3 text-right num text-white/85">{fmt$(px)}</td>
                  <td className={`py-2 px-3 text-right num font-medium ${
                    cost == null ? "text-white/40" : cost >= 0 ? "text-[#22C55E]" : "text-[#EF4444]"
                  }`}>
                    {fmt$(cost)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {(pricing || greeks) && (
        <div className="px-4 py-2 border-t border-white/10 text-[10.5px] text-white/45 num flex flex-wrap gap-x-6 gap-y-1">
          {pricing?.method && <span>pricing · <b className="text-white/75 capitalize">{pricing.method}</b></span>}
          {greeks && (
            <>
              <span>Δ <b className="text-white/85">{fmt$(greeks.delta, 2)}</b></span>
              <span>Γ <b className="text-white/85">{fmt$(greeks.gamma, 4)}</b></span>
              <span>Θ <b className="text-white/85">{fmt$(greeks.theta, 2)}</b></span>
              <span>ν <b className="text-white/85">{fmt$(greeks.vega, 2)}</b></span>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ContractValueTable;
